import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getInvoiceById, updateInvoice, updateInvoiceStatus, getClientById } from '../../firebase/database';
import { generateRevenuesInvoicePDF, downloadPDF, openPDFInNewTab, buildRevenuesInvoiceFilename } from '../../services/invoicePdfService';
import { LogisticsInvoice, CostItem, SaleItem } from '../../types';
import { roundMoney, mulMoney, addMoney, subMoney, roundTo } from '../../utils/number';
import toast from 'react-hot-toast';
import LoadingOverlay from '../LoadingOverlay';

const EditInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { currentUser, currentMode } = useAuth();

  // Refs for input fields
  const invoiceDateRef = useRef<HTMLInputElement>(null);
  const invoiceTypeRef = useRef<HTMLSelectElement>(null);
  const shippingMethodRef = useRef<HTMLSelectElement>(null);
  const shipperNameRef = useRef<HTMLInputElement>(null);
  const consigneeRef = useRef<HTMLInputElement>(null);
  const airportOfDepartureRef = useRef<HTMLInputElement>(null);
  const airportOfDestinationRef = useRef<HTMLInputElement>(null);
  const awbNumberRef = useRef<HTMLInputElement>(null);
  const flightNoRef = useRef<HTMLInputElement>(null);
  const airlineNameRef = useRef<HTMLInputElement>(null);
  const gwWeightRef = useRef<HTMLInputElement>(null);
  const packageCountRefs = useRef<(HTMLInputElement | null)[]>([]);
  const portOfLoadingRef = useRef<HTMLInputElement>(null);
  const portOfDischargeRef = useRef<HTMLInputElement>(null);
  const blNumberRef = useRef<HTMLInputElement>(null);
  const shippingLineRef = useRef<HTMLInputElement>(null);
  const vesselVoyageRef = useRef<HTMLInputElement>(null);
  const containerQuantityRef = useRef<HTMLInputElement>(null);
  const containerNumberRefs = useRef<(HTMLInputElement | null)[]>([]);
  const currencyRef = useRef<HTMLSelectElement>(null);
  const exchangeRateRef = useRef<HTMLInputElement>(null);
  const discountRef = useRef<HTMLInputElement>(null);
  const costItemDescriptionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const costItemQuantityRefs = useRef<(HTMLInputElement | null)[]>([]);
  const costItemRateRefs = useRef<(HTMLInputElement | null)[]>([]);
  const saleItemDescriptionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const saleItemQuantityRefs = useRef<(HTMLInputElement | null)[]>([]);
  const saleItemRateRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<LogisticsInvoice | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [error, setError] = useState('');
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [activeTab, setActiveTab] = useState<'cost' | 'sale'>('cost');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading...');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        if (!invoiceId) return;
        const inv = await getInvoiceById(invoiceId);

        if (!inv) {
          setError('Invoice not found');
          setInvoice(null);
          return;
        }

        const client = inv.clientId ? await getClientById(inv.clientId) : null;

        // Initialize optional fields to empty strings to prevent undefined
        const initializedInvoice: LogisticsInvoice = {
          ...inv,
          airportOfDeparture: inv.airportOfDeparture ?? '',
          airportOfDestination: inv.airportOfDestination ?? '',
          awbNumber: inv.awbNumber ?? '',
          flightNo: inv.flightNo ?? '',
          airlineName: inv.airlineName ?? '',
          gwWeight: inv.gwWeight ?? '',
          packageCount: Array.isArray(inv.packageCount) ? inv.packageCount : (inv.packageCount ? [inv.packageCount] : ['']),
          portOfLoading: inv.portOfLoading ?? '',
          portOfDischarge: inv.portOfDischarge ?? '',
          blNumber: inv.blNumber ?? '',
          shippingLine: inv.shippingLine ?? '',
          vesselVoyage: inv.vesselVoyage ?? '',
          containerQuantity: inv.containerQuantity ?? '',
          containerNumber: Array.isArray(inv.containerNumber) ? inv.containerNumber : (inv.containerNumber ? [inv.containerNumber] : ['']),
          consignee: inv.consignee ?? '',
          shipperName: client ? client.name : inv.shipperName ?? '',
          discount: Math.max(0, Math.round(inv.subtotal - inv.total))
        };

        setInvoice(initializedInvoice);
        
        // Initialize cost and sale items
        if (inv.costItems && inv.costItems.length > 0) {
          setCostItems(inv.costItems);
        } else {
          // Create default cost items if none exist
          setCostItems([{
            id: '1',
            description: '',
            quantity: 1,
            rate: 0,
            amount: 0,
            currency: inv.currency || 'PKR'
          }]);
        }
        
        if (inv.saleItems && inv.saleItems.length > 0) {
          setSaleItems(inv.saleItems);
        } else {
          // Create default sale items if none exist
          setSaleItems([{
            id: '1',
            description: '',
            quantity: 1,
            rate: 0,
            amount: 0,
            currency: inv.currency || 'PKR'
          }]);
        }
      } catch (e) {
        console.error('Error loading invoice:', e);
        setError('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invoiceId]);

  const updateField = (field: keyof LogisticsInvoice, value: any) => {
    if (!invoice) return;
    if (field === 'shipperName') return;

    // Ensure string fields are never undefined
    const safeValue = Array.isArray(value) ? value : (typeof value === 'string' ? value : value ?? '');
    setInvoice({ ...invoice, [field]: safeValue });

    // If shippingMethod changes, initialize relevant fields
    if (field === 'shippingMethod') {
      const updatedInvoice = { ...invoice, [field]: value };
      if (value === 'air') {
        updatedInvoice.portOfLoading = '';
        updatedInvoice.portOfDischarge = '';
        updatedInvoice.blNumber = '';
        updatedInvoice.shippingLine = '';
        updatedInvoice.vesselVoyage = '';
        updatedInvoice.containerQuantity = '';
        updatedInvoice.containerNumber = [''];
      } else if (value === 'sea') {
        updatedInvoice.airportOfDeparture = '';
        updatedInvoice.airportOfDestination = '';
        updatedInvoice.awbNumber = '';
        updatedInvoice.flightNo = '';
        updatedInvoice.airlineName = '';
        updatedInvoice.gwWeight = '';
        updatedInvoice.packageCount = [''];
      }
      setInvoice(updatedInvoice);
    }
  };


  // Cost item functions
  const updateCostItem = (id: string, field: keyof CostItem, value: string | number) => {
    setCostItems(costItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'rate' || field === 'currency') {
          updatedItem.amount = mulMoney(updatedItem.quantity, updatedItem.rate);
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const addCostItem = () => {
    const newItem: CostItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: invoice?.currency || 'PKR'
    };
    setCostItems([...costItems, newItem]);
  };

  const removeCostItem = (id: string) => {
    if (costItems.length > 1) {
      setCostItems(costItems.filter(item => item.id !== id));
    }
  };

  // Sale item functions
  const updateSaleItem = (id: string, field: keyof SaleItem, value: string | number) => {
    setSaleItems(saleItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'rate' || field === 'currency') {
          updatedItem.amount = mulMoney(updatedItem.quantity, updatedItem.rate);
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const addSaleItem = () => {
    const newItem: SaleItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: invoice?.currency || 'PKR'
    };
    setSaleItems([...saleItems, newItem]);
  };

  const removeSaleItem = (id: string) => {
    if (saleItems.length > 1) {
      setSaleItems(saleItems.filter(item => item.id !== id));
    }
  };

  const subtotalPKR = useMemo(() => {
    if (!invoice) return 0;
    // Use sale items for calculations
    const total = saleItems.reduce((sum, it) => {
      const itemTotal = it.currency !== 'PKR' ? mulMoney(it.amount, invoice.exchangeRate) : it.amount;
      return addMoney(sum, itemTotal);
    }, 0);
    return roundMoney(total);
  }, [invoice, saleItems]);

  const discountPKR = useMemo(() => {
    if (!invoice) return 0;
    return roundMoney(invoice.discount || 0);
  }, [invoice]);

  const totalPKR = useMemo(() => subMoney(subtotalPKR, discountPKR), [subtotalPKR, discountPKR]);

  // Calculate cost totals (using cost items)
  const costTotalPKR = useMemo(() => {
    if (!invoice) return 0;
    const total = costItems.reduce((sum, item) => {
      if (item.currency !== 'PKR') {
        return addMoney(sum, mulMoney(item.amount, invoice.exchangeRate));
      }
      return addMoney(sum, item.amount);
    }, 0);
    return roundMoney(total);
  }, [costItems, invoice]);

  const profitPKR = useMemo(() => subMoney(subtotalPKR, costTotalPKR), [subtotalPKR, costTotalPKR]);

  // Validate draft form - only requires client and company
  const validateDraftForm = (): boolean => {
    if (!invoice) return false;

    const errors: Record<string, string> = {};
    
    // Only client and company selection are required for draft
    if (!invoice.clientId) {
      errors['clientId'] = 'Required';
    }
    
    if (!invoice.logisticsCompany) {
      errors['logisticsCompany'] = 'Required';
    }
    
    if (!invoice.clientId || !invoice.logisticsCompany) {
      toast.error('Both Party and Company selection are required to save draft', { position: 'bottom-right' });
    }
    
    setFieldErrors(errors);
    
    return Object.keys(errors).length === 0;
  };

  const validateForm = (): boolean => {
    if (!invoice) return false;

    const errors: Record<string, string> = {};
    if (!invoice.date) errors['invoiceDate'] = 'Required';
    if (!invoice.invoiceType) errors['invoiceType'] = 'Required';
    if (!invoice.shippingMethod) errors['shippingMethod'] = 'Required';
    if (!invoice.shipperName) errors['shipperName'] = 'Required';
    if (!invoice.consignee) errors['consignee'] = 'Required';
    if (!invoice.currency) errors['currency'] = 'Required';
    if (!invoice.exchangeRate || invoice.exchangeRate <= 0) errors['exchangeRate'] = 'Exchange rate must be greater than 0';
    if (invoice.discount && invoice.discount < 0) errors['discount'] = 'Deduction must be 0 or more';

    if (invoice.shippingMethod === 'air') {
      if (!invoice.airportOfDeparture) errors['airportOfDeparture'] = 'Required';
      if (!invoice.airportOfDestination) errors['airportOfDestination'] = 'Required';
      if (!invoice.awbNumber) errors['awbNumber'] = 'Required';
      if (!invoice.flightNo) errors['flightNo'] = 'Required';
      if (!invoice.airlineName) errors['airlineName'] = 'Required';
      if (!invoice.gwWeight) errors['gwWeight'] = 'Required';
      const hasPackage = Array.isArray(invoice.packageCount) ? invoice.packageCount.some(v => v && v.trim() !== '') : !!invoice.packageCount;
      if (!hasPackage) errors['packageCount'] = 'Required';
    } else if (invoice.shippingMethod === 'sea') {
      if (!invoice.portOfLoading) errors['portOfLoading'] = 'Required';
      if (!invoice.portOfDischarge) errors['portOfDischarge'] = 'Required';
      if (!invoice.blNumber) errors['blNumber'] = 'Required';
      if (!invoice.shippingLine) errors['shippingLine'] = 'Required';
      if (!invoice.vesselVoyage) errors['vesselVoyage'] = 'Required';
      if (!invoice.containerQuantity) errors['containerQuantity'] = 'Required';
      const hasContainer = Array.isArray(invoice.containerNumber) ? invoice.containerNumber.some(v => v && v.trim() !== '') : !!invoice.containerNumber;
      if (!hasContainer) errors['containerNumber'] = 'Required';
    }

    // Validate cost items
    costItems.forEach((item, index) => {
      if (!item.description) errors[`cost_item_${index}_description`] = 'Required';
      if (item.quantity <= 0) errors[`cost_item_${index}_quantity`] = 'Must be > 0';
      if (item.rate <= 0) errors[`cost_item_${index}_rate`] = 'Must be > 0';
    });

    // Validate sale items
    saleItems.forEach((item, index) => {
      if (!item.description) errors[`sale_item_${index}_description`] = 'Required';
      if (item.quantity <= 0) errors[`sale_item_${index}_quantity`] = 'Must be > 0';
      if (item.rate <= 0) errors[`sale_item_${index}_rate`] = 'Must be > 0';
    });


    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setTimeout(() => scrollToFirstEmptyField(errors), 100);
    }

    return Object.keys(errors).length === 0;
  };

  const scrollToFirstEmptyField = (errors: Record<string, string>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    const fieldPriority = [
      'invoiceDate',
      'invoiceType',
      'shippingMethod',
      'shipperName',
      'consignee',
      'airportOfDeparture',
      'airportOfDestination',
      'awbNumber',
      'flightNo',
      'airlineName',
      'gwWeight',
      'packageCount',
      'portOfLoading',
      'portOfDischarge',
      'blNumber',
      'shippingLine',
      'vesselVoyage',
      'containerQuantity',
      'containerNumber',
      'currency',
      'exchangeRate'
    ];

    let targetField = null;
    for (const priorityField of fieldPriority) {
      if (errors[priorityField]) {
        targetField = priorityField;
        break;
      }
    }

    if (!targetField) {
      for (const errorKey of errorKeys) {
        if (errorKey.startsWith('item_')) {
          targetField = errorKey;
          break;
        }
      }
    }

    if (targetField) {
      let targetRef: React.RefObject<HTMLElement> | null = null;

      switch (targetField) {
        case 'invoiceDate':
          targetRef = invoiceDateRef;
          break;
        case 'invoiceType':
          targetRef = invoiceTypeRef;
          break;
        case 'shippingMethod':
          targetRef = shippingMethodRef;
          break;
        case 'shipperName':
          targetRef = shipperNameRef;
          break;
        case 'consignee':
          targetRef = consigneeRef;
          break;
        case 'airportOfDeparture':
          targetRef = airportOfDepartureRef;
          break;
        case 'airportOfDestination':
          targetRef = airportOfDestinationRef;
          break;
        case 'awbNumber':
          targetRef = awbNumberRef;
          break;
        case 'flightNo':
          targetRef = flightNoRef;
          break;
        case 'airlineName':
          targetRef = airlineNameRef;
          break;
        case 'gwWeight':
          targetRef = gwWeightRef;
          break;
        case 'packageCount':
          targetRef = { current: packageCountRefs.current.find(r => !!r) as any } as any;
          break;
        case 'portOfLoading':
          targetRef = portOfLoadingRef;
          break;
        case 'portOfDischarge':
          targetRef = portOfDischargeRef;
          break;
        case 'blNumber':
          targetRef = blNumberRef;
          break;
        case 'shippingLine':
          targetRef = shippingLineRef;
          break;
        case 'vesselVoyage':
          targetRef = vesselVoyageRef;
          break;
        case 'containerQuantity':
          targetRef = containerQuantityRef;
          break;
        case 'containerNumber':
          targetRef = { current: containerNumberRefs.current.find(r => !!r) as any } as any;
          break;
        case 'currency':
          targetRef = currencyRef;
          break;
        case 'exchangeRate':
          targetRef = exchangeRateRef;
          break;
        default:
          break;
      }

      if (targetRef?.current) {
        targetRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        targetRef.current.focus();
      }
    }
  };

  const buildInvoiceUpdateData = (status: LogisticsInvoice['status']) => {
    if (!invoice) {
      throw new Error('Invoice not loaded');
    }

    return {
      invoiceNumber: invoice.invoiceNumber,
      clientId: invoice.clientId,
      clientName: invoice.clientName,
      invoiceType: invoice.invoiceType,
      date: invoice.date,
      logisticsCompany: invoice.logisticsCompany,
      logoUrl: invoice.logoUrl,
      shippingMethod: invoice.shippingMethod,
      shipperName: invoice.shipperName,
      consignee: invoice.consignee,
      airportOfDeparture: invoice.airportOfDeparture,
      airportOfDestination: invoice.airportOfDestination,
      awbNumber: invoice.awbNumber,
      flightNo: invoice.flightNo,
      airlineName: invoice.airlineName,
      gwWeight: invoice.gwWeight,
      packageCount: (Array.isArray(invoice.packageCount) ? invoice.packageCount : [invoice.packageCount as any]).filter(v => v && v.toString().trim() !== ''),
      portOfLoading: invoice.portOfLoading,
      portOfDischarge: invoice.portOfDischarge,
      blNumber: invoice.blNumber,
      shippingLine: invoice.shippingLine,
      vesselVoyage: invoice.vesselVoyage,
      containerQuantity: invoice.containerQuantity,
      containerNumber: (Array.isArray(invoice.containerNumber) ? invoice.containerNumber : [invoice.containerNumber as any]).filter(v => v && v.toString().trim() !== ''),
      costItems,
      saleItems,
      subtotal: subtotalPKR,
      discount: invoice.discount,
      discountNature: invoice.discountNature || 'deduction',
      total: totalPKR,
      currency: invoice.currency,
      exchangeRate: invoice.exchangeRate,
      status,
      createdBy: invoice.createdBy
    };
  };

  const handleSave = async () => {
    if (!invoiceId || !invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to save invoice.');
      return;
    }
    setError('');

    setIsSaving(true);
    setIsLoading(true);
    setLoadingMessage('Saving invoice...');
    try {
      // Always set status to 'pending' when saving and resubmitting
      const updateData = buildInvoiceUpdateData('pending');

      await updateInvoice(invoiceId, updateData);
      toast.success('Invoice updated and sent for approval');
      navigate('/dashboard');
    } catch (e) {
      console.error('Save error:', e);
      setError(`Failed to save invoice: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
      setIsLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!invoiceId || !invoice) return;
    setShowValidation(true);
    if (!validateDraftForm()) {
      setError('Please select both Party and Company to save draft.');
      return;
    }
    setError('');

    setIsSavingDraft(true);
    setIsLoading(true);
    setLoadingMessage('Saving draft...');
    try {
      const updateData = buildInvoiceUpdateData('draft');

      await updateInvoice(invoiceId, updateData);
      toast.success('Invoice saved as draft');
    } catch (e) {
      console.error('Save draft error:', e);
      setError(`Failed to save draft: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsSavingDraft(false);
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!invoiceId || !invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to approve invoice.');
      return;
    }
    setError('');

    setIsApproving(true);
    setIsLoading(true);
    setLoadingMessage('Approving invoice...');
    try {
      const updateData = buildInvoiceUpdateData('approved');
      const invoicePayload = {
        ...invoice,
        ...updateData
      } as LogisticsInvoice;

      await updateInvoiceStatus(invoiceId, 'approved', currentUser?.id, invoicePayload);
      toast.success('Invoice updated and approved');
      navigate('/pending-approvals');
    } catch (e) {
      console.error('Approve error:', e);
      toast.error('Failed to approve invoice');
    } finally {
      setIsApproving(false);
      setIsLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to preview PDF.');
      return;
    }
    setError('');

    setIsGeneratingPDF(true);
    setIsLoading(true);
    setLoadingMessage('Generating PDF preview...');
    try {
      // Get client address
      const client = await getClientById(invoice.clientId);
      const clientAddress = client?.address;
      
      const pdf = await generateRevenuesInvoicePDF({ ...invoice, subtotal: subtotalPKR, total: totalPKR }, clientAddress);
      openPDFInNewTab(pdf, 'Invoice PDF');
    } catch (e) {
      console.error('PDF error:', e);
      setError('Error generating PDF preview');
    } finally {
      setIsGeneratingPDF(false);
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to download PDF.');
      return;
    }
    setError('');

    setIsGeneratingPDF(true);
    setIsLoading(true);
    setLoadingMessage('Generating PDF download...');
    try {
      const enriched = { ...invoice, subtotal: subtotalPKR, total: totalPKR };
      const client = await getClientById(invoice.clientId);
      const clientAddress = client?.address;
      
      const pdf = await generateRevenuesInvoicePDF(enriched, clientAddress);
      downloadPDF(pdf, buildRevenuesInvoiceFilename(enriched));
    } catch (e) {
      console.error('PDF download error:', e);
      setError('Error downloading PDF');
    } finally {
      setIsGeneratingPDF(false);
      setIsLoading(false);
    }
  };

  const isAuthorizerAmendingPending = currentUser?.role === 'authorizer' && invoice?.status === 'pending';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <LoadingOverlay isVisible={isLoading} message={loadingMessage} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Amend Invoice {invoice.invoiceNumber}</h1>
          <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">Back</button>
        </div>

        {error && (
          <div className="mb-4 text-red-600 text-sm">{error}</div>
        )}

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date</label>
              <input
                ref={invoiceDateRef}
                type="date"
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.date.toISOString().substring(0, 10)}
                onChange={e => updateField('date', new Date(e.target.value))}
              />
              {showValidation && fieldErrors['invoiceDate'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['invoiceDate']}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Type</label>
              <select
                ref={invoiceTypeRef}
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.invoiceType}
                onChange={e => updateField('invoiceType', e.target.value as any)}
              >
                <option value="import">Import</option>
                <option value="export">Export</option>
              </select>
              {showValidation && fieldErrors['invoiceType'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['invoiceType']}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shipping Method</label>
              <select
                ref={shippingMethodRef}
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.shippingMethod}
                onChange={e => updateField('shippingMethod', e.target.value as any)}
              >
                <option value="air">Air</option>
                <option value="sea">Sea</option>
              </select>
              {showValidation && fieldErrors['shippingMethod'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['shippingMethod']}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shipper Name</label>
              <input
                ref={shipperNameRef}
                type="text"
                className="w-full px-3 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                value={invoice.shipperName}
                readOnly
                tabIndex={-1}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Consignee</label>
              <input
                ref={consigneeRef}
                type="text"
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.consignee}
                onChange={e => updateField('consignee', e.target.value)}
              />
              {showValidation && fieldErrors['consignee'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['consignee']}</p>
              )}
            </div>
          </div>

          {invoice.shippingMethod === 'air' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  ref={airportOfDepartureRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Airport of Departure"
                  value={invoice.airportOfDeparture}
                  onChange={e => updateField('airportOfDeparture', e.target.value)}
                />
                {showValidation && fieldErrors['airportOfDeparture'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['airportOfDeparture']}</p>
                )}
              </div>
              <div>
                <input
                  ref={airportOfDestinationRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Airport of Destination"
                  value={invoice.airportOfDestination}
                  onChange={e => updateField('airportOfDestination', e.target.value)}
                />
                {showValidation && fieldErrors['airportOfDestination'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['airportOfDestination']}</p>
                )}
              </div>
              <div>
                <input
                  ref={awbNumberRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="AWB Number"
                  value={invoice.awbNumber}
                  onChange={e => updateField('awbNumber', e.target.value)}
                />
                {showValidation && fieldErrors['awbNumber'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['awbNumber']}</p>
                )}
              </div>
              <div>
                <input
                  ref={flightNoRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Flight No"
                  value={invoice.flightNo}
                  onChange={e => updateField('flightNo', e.target.value)}
                />
                {showValidation && fieldErrors['flightNo'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['flightNo']}</p>
                )}
              </div>
              <div>
                <input
                  ref={airlineNameRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Airline Name"
                  value={invoice.airlineName}
                  onChange={e => updateField('airlineName', e.target.value)}
                />
                {showValidation && fieldErrors['airlineName'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['airlineName']}</p>
                )}
              </div>
              <div>
                <input
                  ref={gwWeightRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Gross Weight"
                  value={invoice.gwWeight}
                  onChange={e => updateField('gwWeight', e.target.value)}
                />
                {showValidation && fieldErrors['gwWeight'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['gwWeight']}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-700">Package Count</label>
                  <button
                    type="button"
                    onClick={() => updateField('packageCount', [...(invoice.packageCount as any[]), ''])}
                    className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2">
                  {(invoice.packageCount as any[]).map((val: string, idx: number) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        ref={el => (packageCountRefs.current[idx] = el)}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder={idx === 0 ? 'Package Count' : 'Additional package'}
                        value={val}
                        onChange={e => {
                          const next = [...(invoice.packageCount as any[])];
                          next[idx] = e.target.value;
                          updateField('packageCount', next);
                        }}
                      />
                      {(invoice.packageCount as any[]).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = (invoice.packageCount as any[]).filter((_, i) => i !== idx);
                            updateField('packageCount', next);
                          }}
                          className="px-2 py-2 text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                          title="Remove"
                        >
                          −
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {showValidation && fieldErrors['packageCount'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['packageCount']}</p>
                )}
              </div>
            </div>
          )}

          {invoice.shippingMethod === 'sea' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  ref={portOfLoadingRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Port of Loading"
                  value={invoice.portOfLoading}
                  onChange={e => updateField('portOfLoading', e.target.value)}
                />
                {showValidation && fieldErrors['portOfLoading'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['portOfLoading']}</p>
                )}
              </div>
              <div>
                <input
                  ref={portOfDischargeRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Port of Discharge"
                  value={invoice.portOfDischarge}
                  onChange={e => updateField('portOfDischarge', e.target.value)}
                />
                {showValidation && fieldErrors['portOfDischarge'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['portOfDischarge']}</p>
                )}
              </div>
              <div>
                <input
                  ref={blNumberRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="BL Number"
                  value={invoice.blNumber}
                  onChange={e => updateField('blNumber', e.target.value)}
                />
                {showValidation && fieldErrors['blNumber'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['blNumber']}</p>
                )}
              </div>
              <div>
                <input
                  ref={shippingLineRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Shipping Line"
                  value={invoice.shippingLine}
                  onChange={e => updateField('shippingLine', e.target.value)}
                />
                {showValidation && fieldErrors['shippingLine'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['shippingLine']}</p>
                )}
              </div>
              <div>
                <input
                  ref={vesselVoyageRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Vessel/Voyage"
                  value={invoice.vesselVoyage}
                  onChange={e => updateField('vesselVoyage', e.target.value)}
                />
                {showValidation && fieldErrors['vesselVoyage'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['vesselVoyage']}</p>
                )}
              </div>
              <div>
                <input
                  ref={containerQuantityRef}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Quantity of Container"
                  value={invoice.containerQuantity}
                  onChange={e => updateField('containerQuantity', e.target.value)}
                />
                {showValidation && fieldErrors['containerQuantity'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['containerQuantity']}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-700">Container Number</label>
                  <button
                    type="button"
                    onClick={() => updateField('containerNumber', [...(invoice.containerNumber as any[]), ''])}
                    className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2">
                  {(invoice.containerNumber as any[]).map((val: string, idx: number) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        ref={el => (containerNumberRefs.current[idx] = el)}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder={idx === 0 ? 'Container Number' : 'Additional container number'}
                        value={val}
                        onChange={e => {
                          const next = [...(invoice.containerNumber as any[])];
                          next[idx] = e.target.value;
                          updateField('containerNumber', next);
                        }}
                      />
                      {(invoice.containerNumber as any[]).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = (invoice.containerNumber as any[]).filter((_, i) => i !== idx);
                            updateField('containerNumber', next);
                          }}
                          className="px-2 py-2 text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                          title="Remove"
                        >
                          −
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {showValidation && fieldErrors['containerNumber'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['containerNumber']}</p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
              <select
                ref={currencyRef}
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.currency}
                onChange={e => updateField('currency', e.target.value)}
              >
                <option value="PKR">PKR</option>
                <option value="USD">USD</option>
                <option value="AED">AED</option>
              </select>
              {showValidation && fieldErrors['currency'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['currency']}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Exchange Rate</label>
              <input
                ref={exchangeRateRef}
                type="number"
                step="0.01"
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.exchangeRate}
                onChange={e => updateField('exchangeRate', roundTo(parseFloat(e.target.value) || 1, 4))}
              />
              {showValidation && fieldErrors['exchangeRate'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['exchangeRate']}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Deduction (PKR)</label>
              <input
                ref={discountRef}
                type="number"
                step="0.01"
                min={0}
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.discount || 0}
                onChange={e => {
                  const next = parseFloat(e.target.value);
                  const parsed = isNaN(next) ? 0 : next;
                  updateField('discount', Math.max(0, roundMoney(parsed)));
                }}
              />
              {showValidation && fieldErrors['discount'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['discount']}</p>
              )}
            </div>
          </div>

          <div>
            <div className="mb-4">
              <h3 className="font-medium text-gray-900">Invoice Items</h3>
              <p className="text-sm text-gray-600 mt-1">Edit both Cost and Sale items as needed</p>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-6">
              <button
                type="button"
                onClick={() => setActiveTab('cost')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'cost'
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cost Items
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('sale')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'sale'
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Sale Items
              </button>
            </div>

            {/* Cost Items Tab */}
            {activeTab === 'cost' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Cost Items:</strong> Internal tracking items that won't appear on the PDF or customer-facing documents.
                  </p>
                </div>
                {costItems.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Cost Item {index + 1}</h4>
                      {costItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCostItem(item.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Item Description *</label>
                        <input
                          ref={(el) => (costItemDescriptionRefs.current[index] = el)}
                          value={item.description}
                          onChange={(e) => updateCostItem(item.id, 'description', e.target.value)}
                          placeholder="Enter or select item description"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          required
                        />
                        {showValidation && fieldErrors[`cost_item_${index}_description`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`cost_item_${index}_description`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                        <input
                          ref={(el) => (costItemQuantityRefs.current[index] = el)}
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateCostItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="1"
                          min="1"
                          required
                        />
                        {showValidation && fieldErrors[`cost_item_${index}_quantity`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`cost_item_${index}_quantity`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Rate *</label>
                        <input
                          ref={(el) => (costItemRateRefs.current[index] = el)}
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => updateCostItem(item.id, 'rate', roundMoney(parseFloat(e.target.value) || 0))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="0.00"
                          required
                        />
                        {showValidation && fieldErrors[`cost_item_${index}_rate`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`cost_item_${index}_rate`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                        <select
                          value={item.currency}
                          onChange={(e) => updateCostItem(item.id, 'currency', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        >
                          <option value="PKR">PKR</option>
                          <option value="USD">USD</option>
                          <option value="AED">AED</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Total Cost:</span>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900">
                            {item.currency} {roundMoney(item.amount).toFixed(2)}
                          </div>
                          {item.currency !== 'PKR' && (
                            <div className="text-xs text-blue-600">
                              PKR: {roundMoney(mulMoney(item.amount, invoice?.exchangeRate || 1)).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={addCostItem}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors duration-200"
                  >
                    + Add Cost Item
                  </button>
                </div>
              </div>
            )}

            {/* Sale Items Tab */}
            {activeTab === 'sale' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>Sale Items:</strong> These items will appear on the PDF and in ledgers. This is what the customer sees.
                  </p>
                </div>
                {saleItems.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Sale Item {index + 1}</h4>
                      {saleItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSaleItem(item.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Item Description *</label>
                        <input
                          ref={(el) => (saleItemDescriptionRefs.current[index] = el)}
                          value={item.description}
                          onChange={(e) => updateSaleItem(item.id, 'description', e.target.value)}
                          placeholder="Enter or select item description"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          required
                        />
                        {showValidation && fieldErrors[`sale_item_${index}_description`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`sale_item_${index}_description`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                        <input
                          ref={(el) => (saleItemQuantityRefs.current[index] = el)}
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateSaleItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="1"
                          min="1"
                          required
                        />
                        {showValidation && fieldErrors[`sale_item_${index}_quantity`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`sale_item_${index}_quantity`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Rate *</label>
                        <input
                          ref={(el) => (saleItemRateRefs.current[index] = el)}
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => updateSaleItem(item.id, 'rate', roundMoney(parseFloat(e.target.value) || 0))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="0.00"
                          required
                        />
                        {showValidation && fieldErrors[`sale_item_${index}_rate`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`sale_item_${index}_rate`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                        <select
                          value={item.currency}
                          onChange={(e) => updateSaleItem(item.id, 'currency', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        >
                          <option value="PKR">PKR</option>
                          <option value="USD">USD</option>
                          <option value="AED">AED</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Total Sale:</span>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900">
                            {item.currency} {roundMoney(item.amount).toFixed(2)}
                          </div>
                          {item.currency !== 'PKR' && (
                            <div className="text-xs text-blue-600">
                              PKR: {roundMoney(mulMoney(item.amount, invoice?.exchangeRate || 1)).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={addSaleItem}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors duration-200"
                  >
                    + Add Sale Item
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <div className="flex justify-between text-sm"><span>Sale Subtotal (PKR):</span><span>{subtotalPKR.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm text-blue-600"><span>Cost Total (PKR):</span><span>{costTotalPKR.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm font-bold text-green-600 border-t pt-1"><span>Profit (PKR):</span><span>{profitPKR.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span>Deduction:</span><span>{discountPKR.toFixed(2)}</span></div>
              <div className="flex justify-between text-base font-bold border-t pt-2"><span>Final Total (PKR):</span><span>{totalPKR.toFixed(2)}</span></div>
              {subtotalPKR > 0 && (
                <div className="flex justify-between text-xs text-green-600 font-medium mt-1">
                  <span>Profit margin:</span><span>{((profitPKR / subtotalPKR) * 100).toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              <button onClick={handlePreview} disabled={isGeneratingPDF} className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50">{isGeneratingPDF ? 'Generating...' : 'Preview PDF'}</button>
              <button onClick={handleDownloadPDF} disabled={isGeneratingPDF} className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50">{isGeneratingPDF ? 'Generating...' : 'Download PDF'}</button>
              {!isAuthorizerAmendingPending && (
                <>
                  <button onClick={handleSaveDraft} disabled={isSavingDraft} className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50">{isSavingDraft ? 'Saving...' : 'Save as Draft'}</button>
                  <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{isSaving ? 'Saving...' : 'Save & Resubmit'}</button>
                </>
              )}
            </div>
            {currentUser?.role === 'authorizer' && currentMode !== 'inputter' && (
              <button onClick={handleApprove} disabled={isApproving} className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">{isApproving ? 'Approving...' : 'Approve'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInvoice;